import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the token structure based on what useOAuth returns
export interface OAuthTokenData {
  accessToken: string;
  refreshToken?: string;
  tokenExpiry?: number | string;
  expiresIn?: number;
  email?: string;
  provider?: string;
}

interface OAuthTestState {
  providerTokens: Record<string, OAuthTokenData | null>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
}

const initialState: OAuthTestState = {
  providerTokens: {},
  loading: {},
  errors: {}
};

export const oauthTestSlice = createSlice({
  name: 'oauthTest',
  initialState,
  reducers: {
    setOAuthTokens: (state, action: PayloadAction<{ provider: string; tokens: OAuthTokenData }>) => {
      const { provider, tokens } = action.payload;
      state.providerTokens[provider] = tokens;
      state.errors[provider] = null;
    },
    setOAuthLoading: (state, action: PayloadAction<{ provider: string; loading: boolean }>) => {
      const { provider, loading } = action.payload;
      state.loading[provider] = loading;
    },
    setOAuthError: (state, action: PayloadAction<{ provider: string; error: string }>) => {
      const { provider, error } = action.payload;
      state.errors[provider] = error;
    },
    clearOAuthTokens: (state, action: PayloadAction<{ provider: string }>) => {
      const { provider } = action.payload;
      state.providerTokens[provider] = null;
    },
    loadStoredOAuthTokens: (state) => {
      try {
        const storedTokens = localStorage.getItem('oauthTestTokens');
        if (storedTokens) {
          const parsedTokens = JSON.parse(storedTokens) as Record<string, OAuthTokenData>;
          Object.keys(parsedTokens).forEach(provider => {
            state.providerTokens[provider] = parsedTokens[provider];
          });
        }
      } catch (error) {
        console.error('Failed to load OAuth tokens from storage:', error);
      }
    }
  }
});

export const { 
  setOAuthTokens, 
  setOAuthLoading, 
  setOAuthError, 
  clearOAuthTokens,
  loadStoredOAuthTokens
} = oauthTestSlice.actions;

// Selectors that work with WebRootState
export const selectOAuthTokens = (state: any, provider: string) => 
  state.OAuthTestReducer?.providerTokens?.[provider] || null;

export const selectOAuthLoading = (state: any, provider: string) => 
  state.OAuthTestReducer?.loading?.[provider] || false;

export const selectOAuthError = (state: any, provider: string) => 
  state.OAuthTestReducer?.errors?.[provider] || null;

// Helper selector to check if Facebook is connected for Instagram
export const selectFacebookTokens = (state: any) => 
  state.OAuthTestReducer?.providerTokens?.['facebook'] || null;

export const selectInstagramAvailable = (state: any) => 
  Boolean(state.OAuthTestReducer?.providerTokens?.['facebook']?.accessToken);

// The reducer is exported as default
export default oauthTestSlice.reducer;
